import React, { type HTMLProps, useEffect } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'

import * as CurrentSeason from './CurrentSeason/Content'
import * as UpcomingSeason from './UpcomingSeason/Content'

import useBreakpoints from '@/hooks/useBreakpoints'
import Modal from '@/components/Modals/ModalV2/BaseModal'
import { useArcade } from '@/features/arcade/provider'
import CloudinaryImage from '@/components/CloudinaryImage'

const BackgroundImage = ({
  imgId,
  className,
  position
}: {
  imgId: string
  className?: string
  position?: string
}) => {
  const mediaPath = 'global/arcade/'

  return (
    <CloudinaryImage
      className={className}
      imageId={imgId}
      path={mediaPath}
      layout="fill"
      objectPosition={position}
      objectFit="cover"
    />
  )
}

const useDisclosureContent = () => {
  const { isArcadeLoaded, season } = useArcade()
  if (!isArcadeLoaded) return null
  return season.isCurrentSeason ? CurrentSeason : UpcomingSeason
}

const DisclosureModal = NiceModal.create(({ ...modalProps }) => {
  const { hide, visible, remove } = useModal()

  return (
    <Modal
      overlayHeaderOpaqueOnScroll={true}
      renderInlineTitle={false}
      isOpen={visible}
      className="max-w-full shadow-arcade-top border-t-1 border-arcadeGreen"
      hide={hide}
      onAfterClose={remove}
      {...modalProps}
    >
      {({ ModalBody, ModalFooter }) => {
        const Content = useDisclosureContent()
        if (!Content) return null
        const { Modal } = Content

        return (
          <div className="relative pt-5 w-full">
            <BackgroundImage imgId="arcadeDropDownBgSmall" position="top" />
            <ModalBody className="relative px-1">
              <Modal.Body />
            </ModalBody>
            <ModalFooter className="bg-transparent !mx-0">
              <Modal.Footer />
            </ModalFooter>
          </div>
        )
      }}
    </Modal>
  )
})

export const useDisclosureModal = ({ isOpen }: { isOpen: boolean }) => {
  const { isMobile } = useBreakpoints()
  const dropdownModal = useModal(DisclosureModal)

  const show = () => {
    dropdownModal.show().then()
  }

  const close = () => {
    dropdownModal.hide().then()
  }

  useEffect(() => {
    if (!isMobile) {
      close()
      return
    }

    isOpen ? show() : close()
  }, [isMobile, isOpen])
}

export const DisclosurePopover = ({
  isOpen,
  ...restProps
}: { isOpen: boolean } & HTMLProps<HTMLDivElement>) => {
  const Content = useDisclosureContent()
  const { season } = useArcade()

  const imgId = season?.isCurrentSeason
    ? 'arcadeDropDownBg'
    : 'arcadeDropDownBgSmall'

  if (!Content) return null
  if (!isOpen) return null

  const { Popover } = Content

  return (
    <div className="pt-2 bg-transparent cursor-default" {...restProps}>
      <div className="absolute left-1/2 -translate-x-1/2 min-w-[280px] p-1 overflow-hidden bg-gray-850 rounded-3xl shadow-arcade border-arcadeGreen border-1">
        <BackgroundImage imgId={imgId} className="-z-10" />
        <Popover />
      </div>
    </div>
  )
}
